import {
  Drawer as MantineDrawer,
  HoverCard as MantineHoverCard,
  Menu as MantineMenu,
  Modal as MantineModal,
  Popover as MantinePopover,
  Transition as MantineTransition,
} from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

import styles from "./overlays.module.css";

/** Positions supported by Mantine's floating overlays at current call sites. */
export type OverlayPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end";

export type OverlayOffset = number | { mainAxis?: number; crossAxis?: number; alignmentAxis?: number };

export type OverlayTransition =
  | "pop"
  | "pop-bottom-right"
  | {
      in: React.CSSProperties;
      out: React.CSSProperties;
      common?: React.CSSProperties;
      transitionProperty: React.CSSProperties["transitionProperty"];
    };

export interface OverlayTransitionProps {
  duration?: number;
  exitDuration?: number;
  timingFunction?: string;
  transition?: OverlayTransition;
}

export interface ModalOverlayProps extends ElementProps<"div">, StyleProps {
  opacity?: number;
  color?: string;
  blur?: number | string;
}

export interface ModalCloseButtonProps extends ElementProps<"button">, StyleProps {}

export type ModalSize = SizeValue;

/** ShelfCycle's public Modal surface. Mantine types stay inside this module. */
export interface ModalProps {
  opened: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: React.ReactNode;
  size?: ModalSize;
  centered?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  padding?: SizeValue;
  overlayProps?: ModalOverlayProps;
  keepMounted?: boolean;
  withCloseButton?: boolean;
  closeButtonProps?: ModalCloseButtonProps;
  transitionProps?: OverlayTransitionProps;
  withinPortal?: boolean;
  zIndex?: number;
  shadow?: string;
  headerClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  closeButtonClassName?: string;
}

export type ModalRootProps = Omit<
  ModalProps,
  | "title"
  | "overlayProps"
  | "closeButtonProps"
  | "withCloseButton"
  | "headerClassName"
  | "contentClassName"
  | "bodyClassName"
  | "closeButtonClassName"
>;
export interface ModalContentProps extends ElementProps<"section">, StyleProps {}
export interface ModalHeaderProps extends ElementProps<"div">, StyleProps {}
export interface ModalTitleProps extends ElementProps<"h2">, StyleProps {}
export interface ModalBodyProps extends ElementProps<"div">, StyleProps {}

/**
 * Mantine 6 animated Modal content with `pop` over 200ms, and kept the 200ms when a call site passed
 * `transitionProps` without a duration. Mantine 7 defaults to `fade-down` and leaves such a duration
 * to Transition's own 250ms (`ModalRoot.mjs`, `ModalBaseContent.mjs`), so the wrapper restates both
 * Mantine 6 values; anything a call site passes still wins (SC-2703).
 */
const withModalTransition = (transitionProps?: OverlayTransitionProps): OverlayTransitionProps => ({
  ...transitionProps,
  transition: transitionProps?.transition ?? "pop",
  duration: transitionProps?.duration ?? 200,
});

/**
 * Mantine 7 renamed the overlay's background alpha from `opacity` to `backgroundOpacity`; its
 * `opacity` is now the CSS style prop, which would fade the whole overlay instead. The owned name is
 * unchanged, and an omitted value stays out so Mantine's own 0.6 default applies (SC-2703).
 */
type VendorOverlayProps = Omit<ModalOverlayProps, "opacity"> & { backgroundOpacity?: number };

const toVendorOverlayProps = ({ opacity, ...props }: ModalOverlayProps): VendorOverlayProps =>
  opacity === undefined ? props : { ...props, backgroundOpacity: opacity };

/**
 * Mantine accepts a wider vendor-only prop map than the application exposes. These casts are
 * private adapters: `overlays-casts.test.ts` pins each owned forwarded key, and each translated
 * key's Mantine 7 target, against Mantine's own props.
 */
const ModalRootAdapter = MantineModal.Root as React.ComponentType<ModalRootProps>;
const ModalRoot: React.ComponentType<ModalRootProps> = ({ transitionProps, ...props }: ModalRootProps) => (
  <ModalRootAdapter {...props} transitionProps={withModalTransition(transitionProps)} />
);
const ModalOverlayAdapter = MantineModal.Overlay as React.ForwardRefExoticComponent<
  VendorOverlayProps & React.RefAttributes<HTMLDivElement>
>;
const ModalOverlay = React.forwardRef<HTMLDivElement, ModalOverlayProps>(function ModalOverlay(props, ref) {
  return <ModalOverlayAdapter {...toVendorOverlayProps(props)} ref={ref} />;
});
const ModalContent = MantineModal.Content as React.ForwardRefExoticComponent<ModalContentProps & React.RefAttributes<HTMLElement>>;
const ModalHeader = MantineModal.Header as React.ForwardRefExoticComponent<ModalHeaderProps & React.RefAttributes<HTMLDivElement>>;
const ModalTitle = React.forwardRef<HTMLHeadingElement, ModalTitleProps>(function ModalTitle({ className, ...props }, ref) {
  return <MantineModal.Title {...props} className={[styles.modalTitle, className].filter(Boolean).join(" ")} ref={ref} />;
});
const ModalCloseButton = MantineModal.CloseButton as React.ForwardRefExoticComponent<
  ModalCloseButtonProps & React.RefAttributes<HTMLButtonElement>
>;
const ModalBody = MantineModal.Body as React.ForwardRefExoticComponent<ModalBodyProps & React.RefAttributes<HTMLDivElement>>;

interface MantineModalAdapterProps extends Omit<ModalProps, "overlayProps"> {
  overlayProps?: VendorOverlayProps;
  classNames?: {
    title?: string;
    header?: string;
    content?: string;
    body?: string;
    close?: string;
  };
}
const ModalAdapter = MantineModal as React.ComponentType<MantineModalAdapterProps>;

const ModalBase = ({
  headerClassName,
  contentClassName,
  bodyClassName,
  closeButtonClassName,
  overlayProps,
  transitionProps,
  ...props
}: ModalProps): React.JSX.Element => {
  const classNames = {
    title: styles.modalTitle,
    header: headerClassName,
    content: contentClassName,
    body: bodyClassName,
    close: closeButtonClassName,
  };

  return (
    <ModalAdapter
      {...props}
      overlayProps={overlayProps && toVendorOverlayProps(overlayProps)}
      transitionProps={withModalTransition(transitionProps)}
      classNames={classNames}
    />
  );
};

export const Modal = Object.assign(ModalBase, {
  Root: ModalRoot,
  Overlay: ModalOverlay,
  Content: ModalContent,
  Header: ModalHeader,
  Title: ModalTitle,
  CloseButton: ModalCloseButton,
  Body: ModalBody,
});

export interface PopoverProps {
  children: React.ReactNode;
  width?: number | string;
  opened?: boolean;
  defaultOpened?: boolean;
  position?: OverlayPosition;
  withinPortal?: boolean;
  withArrow?: boolean;
  shadow?: string;
  offset?: OverlayOffset;
  transitionProps?: OverlayTransitionProps;
  keepMounted?: boolean;
  onChange?: (opened: boolean) => void;
  arrowSize?: number;
  arrowOffset?: number;
  arrowRadius?: number;
  arrowPosition?: "center" | "side";
  zIndex?: React.CSSProperties["zIndex"];
  dropdownClassName?: string;
  arrowClassName?: string;
}

export interface OverlayTargetProps {
  children: React.ReactElement;
  refProp?: string;
}
export interface OverlayDropdownProps extends ElementProps<"div">, StyleProps {}

const PopoverTarget = MantinePopover.Target as React.ForwardRefExoticComponent<OverlayTargetProps & React.RefAttributes<HTMLElement>>;
const PopoverDropdown = MantinePopover.Dropdown as React.ComponentType<OverlayDropdownProps>;
interface MantinePopoverAdapterProps extends PopoverProps {
  classNames?: { dropdown?: string; arrow?: string };
}
const PopoverAdapter = MantinePopover as React.ComponentType<MantinePopoverAdapterProps>;

/**
 * Mantine 7 flipped Popover's `withinPortal` default to `true` (`Popover.mjs`); Mantine 6 rendered
 * inline, and INDEX records that as the owned default. Menu and HoverCard render through Popover and
 * restate the same default below (SC-2703).
 */
const PopoverBase = ({ dropdownClassName, arrowClassName, withinPortal = false, ...props }: PopoverProps): React.JSX.Element => {
  const classNames = dropdownClassName || arrowClassName ? { dropdown: dropdownClassName, arrow: arrowClassName } : undefined;
  return <PopoverAdapter {...props} withinPortal={withinPortal} classNames={classNames} />;
};

export const Popover = Object.assign(PopoverBase, {
  Target: PopoverTarget,
  Dropdown: PopoverDropdown,
});

export interface MenuProps {
  children?: React.ReactNode;
  width?: number | string;
  position?: OverlayPosition;
  withinPortal?: boolean;
  withArrow?: boolean;
  shadow?: string;
  offset?: OverlayOffset;
  transitionProps?: OverlayTransitionProps;
  keepMounted?: boolean;
  closeOnItemClick?: boolean;
  arrowSize?: number;
  arrowOffset?: number;
  arrowPosition?: "center" | "side";
  trigger?: "click" | "hover";
  dropdownClassName?: string;
  itemClassName?: string;
  /** Fired when the dropdown opens. */
  onOpen?: () => void;
  /** Fired when the dropdown closes. */
  onClose?: () => void;
}

export interface MenuItemProps extends ElementProps<"button">, StyleProps {
  children?: React.ReactNode;
  color?: string;
  closeMenuOnClick?: boolean;
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  component?: React.ElementType;
  href?: string;
}

export interface MenuLabelProps extends ElementProps<"div">, StyleProps {}
export interface MenuDividerProps extends ElementProps<"div">, StyleProps {}

const MenuTarget = MantineMenu.Target as React.ForwardRefExoticComponent<OverlayTargetProps & React.RefAttributes<HTMLElement>>;
const MenuDropdown = MantineMenu.Dropdown as React.ComponentType<OverlayDropdownProps>;
/** Mantine 7 renamed Menu.Item's `icon` to `leftSection`; the owned name is unchanged (SC-2703). */
const MenuItemAdapter = MantineMenu.Item as React.ComponentType<Omit<MenuItemProps, "icon"> & { leftSection?: React.ReactNode }>;
const MenuItem: React.ComponentType<MenuItemProps> = ({ icon, ...props }: MenuItemProps) => (
  <MenuItemAdapter {...props} leftSection={icon} />
);
const MenuLabel = MantineMenu.Label as React.ForwardRefExoticComponent<MenuLabelProps & React.RefAttributes<HTMLDivElement>>;
const MenuDivider = MantineMenu.Divider as React.ForwardRefExoticComponent<MenuDividerProps & React.RefAttributes<HTMLDivElement>>;
interface MantineMenuAdapterProps extends MenuProps {
  trapFocus?: boolean;
  clickOutsideEvents?: string[];
  classNames?: { dropdown?: string; item?: string };
}
const MenuAdapter = MantineMenu as React.ComponentType<MantineMenuAdapterProps>;

/**
 * Mantine 6's Popover closed on an outside mouse or touch press only; Mantine 7's Menu adds
 * `keydown`, so typing anywhere outside a hover-opened menu would close it (`Menu.mjs`).
 */
const MENU_CLICK_OUTSIDE_EVENTS = ["mousedown", "touchstart"];

/**
 * Mantine 6 trapped focus only in a click-triggered Menu (`trapFocus: trigger === "click"`); Mantine 7
 * traps it for every trigger by default. The wrapper restates the Mantine 6 rule along with the
 * inline-by-default portal and the outside-press events (SC-2703).
 */
const MenuBase = ({
  dropdownClassName,
  itemClassName,
  withinPortal = false,
  trigger = "click",
  ...props
}: MenuProps): React.JSX.Element => {
  const classNames = dropdownClassName || itemClassName ? { dropdown: dropdownClassName, item: itemClassName } : undefined;
  return (
    <MenuAdapter
      {...props}
      withinPortal={withinPortal}
      trigger={trigger}
      trapFocus={trigger === "click"}
      clickOutsideEvents={MENU_CLICK_OUTSIDE_EVENTS}
      classNames={classNames}
    />
  );
};

export const Menu = Object.assign(MenuBase, {
  Target: MenuTarget,
  Dropdown: MenuDropdown,
  Item: MenuItem,
  Label: MenuLabel,
  Divider: MenuDivider,
});

export interface HoverCardProps {
  children?: React.ReactNode;
  width?: number | string;
  position?: OverlayPosition;
  withinPortal?: boolean;
  withArrow?: boolean;
  shadow?: string;
  offset?: OverlayOffset;
  arrowPosition?: "center" | "side";
  initiallyOpened?: boolean;
  openDelay?: number;
  onOpen?: () => void;
}

const HoverCardTarget = MantineHoverCard.Target as React.ForwardRefExoticComponent<OverlayTargetProps & React.RefAttributes<HTMLElement>>;
const HoverCardDropdown = MantineHoverCard.Dropdown as React.ComponentType<OverlayDropdownProps>;
const HoverCardAdapter = MantineHoverCard as React.ComponentType<HoverCardProps>;

/** Inline by default, as Mantine 6 rendered it; see PopoverBase (SC-2703). */
const HoverCardBase = ({ withinPortal = false, ...props }: HoverCardProps): React.JSX.Element => (
  <HoverCardAdapter {...props} withinPortal={withinPortal} />
);

export const HoverCard = Object.assign(HoverCardBase, {
  Target: HoverCardTarget,
  Dropdown: HoverCardDropdown,
});

export type DrawerPosition = "top" | "bottom" | "left" | "right";

/** ShelfCycle's public Drawer surface. Its slot class names replace Mantine's `styles` map. */
export interface DrawerProps extends Omit<ElementProps<"div">, "title">, StyleProps {
  opened: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: React.ReactNode;
  position?: DrawerPosition;
  size?: SizeValue;
  padding?: SizeValue;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  keepMounted?: boolean;
  withCloseButton?: boolean;
  withOverlay?: boolean;
  overlayProps?: ModalOverlayProps;
  closeButtonProps?: ModalCloseButtonProps;
  transitionProps?: OverlayTransitionProps;
  withinPortal?: boolean;
  lockScroll?: boolean;
  trapFocus?: boolean;
  returnFocus?: boolean;
  zIndex?: number;
  shadow?: string;
  target?: HTMLElement | string;
  titleClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  closeButtonClassName?: string;
}

interface MantineDrawerAdapterProps extends Omit<DrawerProps, "target" | "overlayProps"> {
  portalProps?: { target?: DrawerProps["target"] };
  overlayProps?: VendorOverlayProps;
  classNames?: {
    title?: string;
    header?: string;
    content?: string;
    body?: string;
    close?: string;
  };
}

/**
 * Mantine carries its drawer style-map types through ModalBase. This private adapter keeps those
 * types out of the public boundary while forwarding every owned prop; overlays-casts pins it.
 */
const DrawerAdapter = MantineDrawer as React.ComponentType<MantineDrawerAdapterProps>;

/**
 * Three Mantine 7 moves, each absorbed here with the owned name unchanged (SC-2703): the portal
 * `target` moved into `portalProps`, the overlay's `opacity` became `backgroundOpacity`, and the
 * content transition lost Mantine 6's 200ms default duration (`DrawerRoot.mjs` sets only the slide),
 * so it falls to Transition's own 250ms unless restated.
 */
export const Drawer = ({
  titleClassName,
  headerClassName,
  contentClassName,
  bodyClassName,
  closeButtonClassName,
  target,
  overlayProps,
  transitionProps,
  ...props
}: DrawerProps): React.JSX.Element => {
  const classNames =
    titleClassName || headerClassName || contentClassName || bodyClassName || closeButtonClassName
      ? {
          title: titleClassName,
          header: headerClassName,
          content: contentClassName,
          body: bodyClassName,
          close: closeButtonClassName,
        }
      : undefined;

  return (
    <DrawerAdapter
      {...props}
      portalProps={target === undefined ? undefined : { target }}
      overlayProps={overlayProps && toVendorOverlayProps(overlayProps)}
      transitionProps={{ ...transitionProps, duration: transitionProps?.duration ?? 200 }}
      classNames={classNames}
    />
  );
};

export type TransitionName =
  | "fade"
  | "skew-up"
  | "skew-down"
  | "rotate-right"
  | "rotate-left"
  | "slide-down"
  | "slide-up"
  | "slide-right"
  | "slide-left"
  | "scale-y"
  | "scale-x"
  | "scale"
  | "pop"
  | "pop-top-left"
  | "pop-top-right"
  | "pop-bottom-left"
  | "pop-bottom-right";

export type TransitionDefinition =
  | TransitionName
  | {
      common?: React.CSSProperties;
      in: React.CSSProperties;
      out: React.CSSProperties;
      transitionProperty: React.CSSProperties["transitionProperty"];
    };

/** Render-prop visibility transition; no timing or mount default is added here. */
export interface TransitionProps {
  mounted: boolean;
  transition: TransitionDefinition;
  children(styles: React.CSSProperties): React.ReactElement;
  keepMounted?: boolean;
  duration?: number;
  exitDuration?: number;
  timingFunction?: string;
  onExited?: () => void;
  onExit?: () => void;
  onEnter?: () => void;
  onEntered?: () => void;
}

export const Transition = (props: TransitionProps): React.JSX.Element => <MantineTransition {...props} />;
